import { ToasterService, BBToasterOptions } from './toaster.service';
import { LocalStorageService } from 'angular-2-local-storage';
import { Mediaclip } from './../interfaces/mediaclip';
import { PublicationService } from './publication.service';
import { TranslateService } from '@ngx-translate/core';
import { BbDatePipe } from './../pipes/bb-date.pipe';
import { ChartDataset } from 'chart.js';
import { map, catchError, takeUntil } from 'rxjs/operators';
import { Observable, ReplaySubject, Subscription, of, forkJoin, Subject } from 'rxjs';
import { HttpParams } from '@angular/common/http';
import { SapiService } from './sapi.service';
import { Injectable } from '@angular/core';
import dayjs from 'dayjs';
import { Publication } from 'app/interfaces/publication';

export interface StatsDateRange {
	fromDate: string;
	toDate: string;
}

export interface AnalyticsFilterConfig {
	label: string;
	name: string;
	category: string;
	type: string;
	solrType?: string;
	aliases?: string[];
	idMapped?: boolean;
	hidden?: boolean;
	allstatsOnly?: boolean;
}

export type AnalyticsGranularity = 'auto' | 'minute' | 'hour' | 'day' | 'week' | 'month' | 'year';

export interface AnalyticsFacetData {
	[facetName: string]: {
		filtered: boolean;
		lineActive: boolean;
		excluded: boolean;
		values?: AnalyticsFacetItem[];
	};
}

export interface AnalyticsFacetConfig {
	not?: boolean;
	query?: string | string[];
	limit?: number;
	facet?: string | string[];
}
export interface AnalyticsAPIResponse {
	body: {
		total: number;
		otype: 'mediaclip';
		items: {
			datetime: string;
			total: number;
			items?: {
				[facetName: string]: AnalyticsFacetData[];
			};
		}[];
		updatedtime: string; // datestring
		facets?: {
			[facetName: string]: AnalyticsFacetData[] | any;
		};
	};
	period: {
		fromDate: string; // datestring
		toDate: string; // datestring
		granularity: AnalyticsGranularity;
	};
	subject: {
		otype: string;
		oid: string;
	};
	subtype: 'range';
	type: 'AnalyticsResponse';
}

export interface AnalyticsRangeDataset {
	type: 'MediaClip';
	total: number;
	chartDataset: ChartDataset;
}

export interface AnalyticsRangeDataItem {
	x: any;
	y: any;
}
export interface AnalyticsFacetItem {
	value: string;
	count: any;
	items?: {
		[facetName: string]: AnalyticsFacetItem[];
	};
}

export interface AnalyticsStatusSet {
	dateRange?: StatsDateRange;
	granularity?: AnalyticsGranularity;
	openCat?: any;
	activeFacets?: {
		[facetName: string]: number;
	};
	filters?: {
		[facetName: string]: string[];
	};
	highlights?: {
		[facetName: string]: string[];
	};
	facetconfig?: {
		[facetName: string]: AnalyticsFacetConfig;
	};
	daterangePreset?: string;
}

@Injectable()
export class AnalyticsService {
	public status: AnalyticsStatusSet = {
		granularity: 'auto',
		openCat: {},
		activeFacets: {},
		filters: {},
		highlights: {},
		facetconfig: {},
		daterangePreset: 'last-30-days'
	};
	public filterConfigs: AnalyticsFilterConfig[];
	public facetData: any = {};
	public facetRangeData: any = {};
	public chosenGranularity: AnalyticsGranularity;
	public loadingData = false;
	public loadingTopClips = false;
	public loadingFacetRef: any = {};
	public numFacetHighlightsLoading = 0;
	public facetHighlightColors = ['#81C946', '#BD10E0', '#40C1C9', '#F77B38', '#FBCA4E', '#AB7DF5'];
	public facetHighlightCount = 0;
	public maxfacetHighlightCount = 6;
	public highlightColorRef: {
		[facetNameAndValue: string]: string;
	} = {};
	public facetValueLabelMap: any = {}; // stores all fetched facetValues with their label so we can map the value to the label when needed

	rangeDataSubject = new ReplaySubject<AnalyticsRangeDataset>(1);
	facetRangeDataSubject = new ReplaySubject<any>(1);
	facetDataSubject = new ReplaySubject(1);
	topClipsSubject = new ReplaySubject(1);
	public statusSubject = new ReplaySubject<AnalyticsStatusSet>(1);
	facetconfigParam: any = {};
	facetGroups: any[];
	httpRequestCanceller = new Subject<void>();
	filterConfigChange = new ReplaySubject<any>(1);
	public metrics: {
		[metricName: string]: {
			type: 'time' | 'int' | 'percentage' | 'float';
			count?: number;
			label: string;
			loading?: boolean;
			description: string;
		};
	};
	public totalViews = null;
	public activeType: string;
	public activeId: string;
	public facetConfigs: any = {};
	public isAllstats = false;
	pctViewedGraphDatasets: any[];
	mediaclip: Mediaclip;
	mediaclipNotFound = false;
	subscriptions = new Subscription();
	private params: HttpParams;

	constructor(
		private sapiService: SapiService,
		private publicationService: PublicationService,
		private toasterService: ToasterService,
		private translate: TranslateService,
		private localStorageService: LocalStorageService,
		private datePipe: BbDatePipe
	) {
		this.subscriptions.add(this.statusSubject.subscribe((status: AnalyticsStatusSet) => {
			this.localStorageService.set('analyticsStatusSet', status);
		}));
		this.subscriptions.add(this.filterConfigChange.subscribe(() => {
			this.filterConfigs.forEach((config) => {
				this.facetConfigs[config.name] = config;
			});
		}));
		const _this = this;
		this.metrics = {
			views: {
				loading: false,
				type: 'int',
				count: null,
				label: this.translate.instant('VIEWS'),
				description: this.translate.instant('HELP.VIEWS')
			},
			playerLoads: {
				loading: false,
				type: 'int',
				count: null,
				label: this.translate.instant('PLAYER_LOADS'),
				description: this.translate.instant('HELP.PLAYER_LOADS')
			},
			playRate: {
				get loading() {
					return _this.metrics?.views?.loading || _this.metrics?.playerLoads?.loading;
				},
				type: 'percentage',
				get count() {
					if (!_this.metrics?.views?.count || !_this.metrics?.playerLoads?.count ) {
						return null;
					}
					return (_this.metrics?.views?.count / _this.metrics?.playerLoads?.count) * 100 || null;
				},
				label: this.translate.instant('PLAY_RATE'),
				description: this.translate.instant('HELP.PLAY_RATE')
			},
			uniqueViewers: {
				loading: false,
				type: 'int',
				count: null,
				label: this.translate.instant('UNIQUE_VIEWERS'),
				description: this.translate.instant('HELP.UNIQUE_VIEWERS')
			},
			viewsPerUniqueViewer: {
				get loading() {
					return _this.metrics?.uniqueViewers?.loading || _this.metrics?.views?.loading;
				},
				type: 'float',
				get count() {
					return (_this.metrics?.views?.count / _this.metrics?.uniqueViewers?.count) || null;
				},
				label: this.translate.instant('VIEWS_PER_UNIQUE_VIEWER'),
				description: this.translate.instant('HELP.VIEWS_PER_UNIQUE_VIEWER')
			}
		};

		this.statusSubject.next(this.status);
		this.setFilterConfigs();

		// Use below to load the last filterset settings from localstorage
		// const statusSet = this.localStorageService.get('analyticsStatusSet');
		// if (statusSet) {
		// 	this.setStatusSet(statusSet);
		// } else {
		// 	this.statusSubject.next(this.status);
		// }
	}

	setDateRange(dateRange: StatsDateRange, updateData = true) {
		this.status.dateRange = dateRange;
		if (updateData) {
			this.update();
		}
	}

	setStatusSet(statusSet: AnalyticsStatusSet) {
		this.status = statusSet;
		this.update();
	}

	clearAllFilters() {
		this.status.filters = {};
		this.update();
	}

	update() {
		// used to cancel previous http requests because new filters were applied
		this.httpRequestCanceller.next();
		this.httpRequestCanceller.complete();
		this.httpRequestCanceller = new Subject<void>();

		this.updateChosenGranularity();
		this.setParams();

		this.getClipRangeStats('').pipe(
			takeUntil(this.httpRequestCanceller)
		).subscribe();
		this.getRangeFacets();
		this.getPlayerLoads();
		this.getUniqueViewers();
		this.getTopClipsList();
		this.statusSubject.next(this.status);
	}

	onRequestError(response) {
		const toastConfig: BBToasterOptions = {
			title: 'Error fetching analytics'
		};
		if (response?.error?.body || !navigator.onLine) {
			toastConfig.message = response?.error?.body || 'No internet connection';
		}
		this.toasterService.error(toastConfig);
	}

	getUniqueViewers(): void | Subscription {
		this.metrics.uniqueViewers.count = null;
		this.metrics.uniqueViewers.loading = true;

		this.setParams('uid');
		let params = this.params;
		params = params.delete('rangefacet');
		params = params.delete('granularity');

		const facetconfigParam = this.facetconfigParam;
		const activeFacetsArr = ['uid'];

		// Filters in facetconfig
		const filterArr: string[] = Object.keys(this.status.filters);
		if (filterArr.length) {
			filterArr.forEach((facetName: string) => {
				if (!activeFacetsArr.includes(facetName)) {
					activeFacetsArr.push(facetName);
				}
			});
		}
		params = params.set('facet', (JSON.stringify(activeFacetsArr)));
		facetconfigParam.uid = {
			limit: 0,
			metric: 'unique'
		};
		params = params.set('facetconfig', (JSON.stringify(facetconfigParam)));

		const url = '/analytics/views/mediaclip';
		return this.sapiService.getUrl(url, params).pipe(
			catchError((err) => {
				this.metrics.uniqueViewers.loading = false;
				this.onRequestError(err);
				return of(null);
			}),
			takeUntil(this.httpRequestCanceller)
		).subscribe((res: AnalyticsAPIResponse) => {
			this.metrics.uniqueViewers.count = res?.body?.facets?.unique_uid || 0;
			this.metrics.uniqueViewers.loading = false;
		});
	}

	getPlayerLoads(clipId: string = null): Subscription {
		this.metrics.playerLoads.count = null;
		this.metrics.playerLoads.loading = true;

		// check for filters and bail out if something other then eid is set as a filter
		let haveActiveFilters = false;
		Object.keys(this.status.filters).forEach((facetName) => {
			if (facetName !== 'eid') {
				haveActiveFilters = true;
			}
		});

		if (haveActiveFilters) {
			// bail out
			this.metrics.playerLoads.loading = false;
			return new Subscription();
		}

		let params = this.params;
		params = params
			.delete('facet')
			.delete('facetconfig')
			.delete('rangefacet')
			.delete('granularity');
		if (this.facetconfigParam.eid) {
			const facetconfigParam = {
				eid: this.facetconfigParam.eid
			};
			params = params.set('facet', 'eid');
			params = params.set('facetconfig', (JSON.stringify(facetconfigParam)));
		}

		let url = '/analytics/inits/mediaclip';
		if (clipId) {
			url += `/${clipId}`;
		}

		return this.sapiService.getUrl(url, params).pipe(
			catchError((err) => {
				this.metrics.playerLoads.loading = false;
				this.onRequestError(err);
				return of(null);
			}),
			takeUntil(this.httpRequestCanceller)
		).subscribe((res: AnalyticsAPIResponse) => {
			this.metrics.playerLoads.count = res?.body?.total;
			this.metrics.playerLoads.loading = false;
		});
	}

	setParams(singleFacetName?: string) {
		this.params = new HttpParams();
		this.facetconfigParam = {};

		// Date range & granularity
		if (this.status.dateRange) {
			this.params = this.params
				.set('fromDate', this.status.dateRange.fromDate)
				.set('toDate', this.status.dateRange.toDate);
		}
		if (!singleFacetName && this.chosenGranularity) {
			this.params = this.params.set('granularity', this.chosenGranularity);
		}

		// Facet counts
		const activeFacetsArr = Object.keys(this.status.activeFacets);
		// Always include country for world map
		if (!singleFacetName && !activeFacetsArr.includes('country')) {
			activeFacetsArr.push('country');
		}
		// Include exactPercentageViewed when in clip details
		if (!singleFacetName && this.activeId && this.activeType === 'mediaclip' && !activeFacetsArr.includes('exactPercentageViewed')) {
			activeFacetsArr.push('exactPercentageViewed');
		}

		if (activeFacetsArr.length) {
			activeFacetsArr.forEach((facetName) => {
				if (!this.facetconfigParam[facetName]) {
					this.facetconfigParam[facetName] = {};
				}
				if (!this.status.facetconfig[facetName]) {
					this.status.facetconfig[facetName] = {};
				}
				this.facetconfigParam[facetName].limit = !singleFacetName || facetName === singleFacetName ? this.status.activeFacets[facetName] : 0;
				this.facetconfigParam[facetName].not = !!this.status.facetconfig[facetName].not;
			});
		}

		if (!singleFacetName) {
			// Country facetdepth of at least 300 for map
			if (!this.facetconfigParam.country.limit || this.facetconfigParam.country.limit < 300) {
				this.facetconfigParam.country.limit = 300;
			}
			if (activeFacetsArr.includes('exactPercentageViewed')) {
				// exactPercentageViewed facetdepth of 100
				if (!this.facetconfigParam.exactPercentageViewed.limit || this.facetconfigParam.exactPercentageViewed.limit < 100) {
					this.facetconfigParam.exactPercentageViewed.limit = 100;
				}
			}
		}


		// Filters in facetconfig
		const filterArr: string[] = Object.keys(this.status.filters);
		if (filterArr.length) {
			filterArr.forEach((facetName: string) => {
				if (!singleFacetName && !activeFacetsArr.includes(facetName)) {
					activeFacetsArr.push(facetName);
				}
				if (!this.facetconfigParam[facetName]) {
					this.facetconfigParam[facetName] = {};
				}
				this.facetconfigParam[facetName].query = this.jsonEncodeArrOrValue(this.status.filters[facetName]);
			});
		}
		// remove empty query arrays
		const facetconfigArr = Object.keys(this.facetconfigParam);
		facetconfigArr.forEach((facetName) => {
			if (!this.facetconfigParam[facetName]) {
				this.facetconfigParam[facetName] = {};
			}
			if (this.facetconfigParam[facetName].query && this.facetconfigParam[facetName].query.length === 0) {
				delete this.facetconfigParam[facetName].query;
			}
			if (Object.keys(this.facetconfigParam[facetName]).length === 0) {
				delete this.facetconfigParam[facetName];
			}
		});

		if (this.activeId && !activeFacetsArr.includes('eid')) {
			activeFacetsArr.push('eid');
		}

		// set facets param to combined activeFacets and filters
		this.params = this.params.set('facet', (JSON.stringify(activeFacetsArr)));

		// rangefacets
		const rangeFacetsArr = Object.keys(this.status.highlights);
		if (rangeFacetsArr.length) {
			this.params = this.params.set('rangefacet', (JSON.stringify(rangeFacetsArr)));
		}


		// facetconfig
		this.params = this.params.set('facetconfig', (JSON.stringify(this.facetconfigParam)));
	}

	// JSON Encode values for backend, except for boolean
	// use this method when the values need to be "exact".
	// Example: a facet for title should be between quotes since the search needs to be for exactly that value.
	jsonEncodeArrOrValue(value: (string | number | boolean)[] | string | number | boolean) {
		if (Array.isArray(value)) {
			return value.map((val: any) => typeof val === 'boolean' ? val : JSON.stringify(val));
		}
		return typeof value === 'boolean' ? value : JSON.stringify(value);
	}

	setFacetCount(facetName: string, count: number, searchTerm?: string) {
		if (count === 0) {
			delete this.status.activeFacets[facetName];
			delete this.facetconfigParam[facetName];
			delete this.facetData[facetName];
			delete this.status.filters[facetName];
			delete this.status.highlights[facetName];
			delete this.facetRangeData[facetName];
			this.facetRangeDataSubject.next(this.facetRangeData);
			this.update();
		} else {
			this.status.activeFacets[facetName] = count;
			this.getFacetCount(facetName, searchTerm);
		}
	}

	getFacetCount(facetName: string, searchTerm?: string): Subscription {
		this.loadingFacetRef[facetName] = true;
		this.updateChosenGranularity();
		this.setParams(facetName);

		this.statusSubject.next(this.status);
		let params = this.params.delete('rangefacet');
		if (searchTerm) {
			this.facetconfigParam[facetName].exclude = false;
			searchTerm = searchTerm.replace(/ /g, `\\ `);
			this.facetconfigParam[facetName].query = searchTerm.includes('"') ? searchTerm : [`${searchTerm}*`, `*${searchTerm}*`];
			params = params.set('facetconfig', JSON.stringify(this.facetconfigParam));
		}

		const req = this.sapiService.getUrl('/analytics/views/mediaclip', params).pipe(
			catchError(err => {
				console.error('Error in getSingleFacet', err);
				this.onRequestError(err);
				return of(null);
			}),
			map((data: AnalyticsAPIResponse) => {
				this.loadingFacetRef = {};
				if (!data) {
					this.facetDataSubject.next(null);
					this.rangeDataSubject.next(null);
					return null;
				}
				if (searchTerm !== undefined && data.body.total === 0) {
					const facets = {};
					facets[facetName] = [];
					this.parseFacetData(facets);
				}
				if (data.body && data.body.facets) {
					this.parseFacetData(data.body.facets);
				}
			})
		);

		return req.pipe(
			takeUntil(this.httpRequestCanceller)
		).subscribe();
		// if (this.httpSub && this.httpSub.closed === false) {
		// 	// requestQueue ?
		// 	this.requestQueue.push(req);
		// } else {
		// 	req.subscribe();
		// }
	}

	toggleFacetExclude(facetName: string) {
		if (!this.facetconfigParam[facetName]) {
			this.facetconfigParam[facetName] = {};
		}
		this.facetconfigParam[facetName].not = !this.facetconfigParam[facetName].not;
		this.update();
	}

	getFacetRange(facetName: string, facetValue: string) {
		const facetLabel = this.facetValueLabelMap?.[facetName]?.[facetValue];
		this.numFacetHighlightsLoading = this.status.highlights[facetName].length;
		this.loadingFacetRef[facetName] = true;
		this.loadingFacetRef[facetName + ':' + facetValue] = true;
		this.setParams(facetName);
		let params = this.params;

		if (!this.facetconfigParam[facetName]) {
			this.facetconfigParam[facetName] = {};
		}
		this.facetconfigParam[facetName].query = this.jsonEncodeArrOrValue(facetValue);
		this.facetconfigParam[facetName].exclude = false;
		params = params.set('facetconfig', (JSON.stringify(this.facetconfigParam)));
		params = params.set('granularity', this.chosenGranularity);
		params = params.set('rangefacet', facetName);

		const req = this.sapiService.getUrl('/analytics/range/mediaclip', params).pipe(
			takeUntil(this.httpRequestCanceller),
			catchError(err => {
				this.loadingFacetRef[facetName + ':' + facetValue] = false;
				this.numFacetHighlightsLoading--;
				if (this.numFacetHighlightsLoading > 0) {
					this.loadingFacetRef[facetName] = true;
				} else {
					this.loadingFacetRef[facetName] = false;
				}
				this.onRequestError(err);
				console.error('Error in getSingleFacet', err);
				return of(null);
			}),
			map((data: AnalyticsAPIResponse) => {
				this.loadingFacetRef[facetName + ':' + facetValue] = false;
				this.numFacetHighlightsLoading--;
				if (this.numFacetHighlightsLoading > 0) {
					this.loadingFacetRef[facetName] = true;
				} else {
					this.loadingFacetRef[facetName] = false;
				}

				const analyticsRangeDataset: AnalyticsRangeDataset = {
					total: 0,
					type: 'MediaClip',
					chartDataset: {
						label: `${this.facetConfigs?.[facetName]?.label || facetName}: ${facetLabel || facetValue}`,
						parsing: false,
						data: [],
						backgroundColor: ['#ff00ff'],
						borderColor: ['#ff0000'],
						borderWidth: 2
					}
				};
				if (!data.body || data.body.items === null) {
					this.rangeDataSubject.next(null);
					return analyticsRangeDataset;
				}

				const dataSet = data.body.items.map(item =>
				// return item.items && item.items[facetName][0].count || 0;
					({
						x: this.getFormattedDate(this.chosenGranularity, item.datetime, data.period, data.body.items),
						y: item.items && item.items[facetName][0].count || 0
					})
				);
				analyticsRangeDataset.total = data.body.total || 0;
				analyticsRangeDataset.chartDataset.data = dataSet as any;
				if (!this.facetRangeData[facetName]) {
					this.facetRangeData[facetName] = {};
				}
				this.facetRangeData[facetName][facetValue] = analyticsRangeDataset;
				return this.facetRangeData;
			}
			));
		return req;
	}

	getTopClipsList() {
		this.loadingTopClips = true;
		this.setParams();
		let params = this.params;
		params = params.delete('rangefacet');

		// set eid
		const newFacetconfig = this.facetconfigParam;
		newFacetconfig.eid = newFacetconfig.eid || {};
		newFacetconfig.eid.facet = 'title';
		newFacetconfig.eid.limit = 10;
		newFacetconfig.eid.exclude = false;
		Object.keys(newFacetconfig).forEach(facetName => {
			if (facetName !== 'eid') {
				if (!newFacetconfig[facetName].query) {
					delete newFacetconfig[facetName];
				} else {
					newFacetconfig[facetName].limit = 0;
				}
			}
		});
		params = params.set('facet', JSON.stringify(Object.keys(newFacetconfig)));
		params = params.set('facetconfig', JSON.stringify(newFacetconfig));

		const req = this.sapiService.getUrl('/analytics/views/mediaclip', params).pipe(
			takeUntil(this.httpRequestCanceller),
			catchError(err => {
				this.loadingTopClips = false;
				console.error('Error in getSingleFacet', err);
				this.onRequestError(err);
				return of(null);
			}),
			map((data: AnalyticsAPIResponse) => {
				this.loadingTopClips = false;
				this.topClipsSubject.next(data?.body?.facets?.eid);
			}
			));
		req.subscribe();
		// https://local.dev.bbvms.com/sapi/analytics/range/mediaclip?granularity=day&facet=eid&facetconfig={%22eid%22:{%22facet%22:%22title%22}}
	}

	setRoute(type: string, id: string) {
		if (!type || type === 'mediaclip') {
			// remove any active filters on eid
			delete this.status.filters.eid;
		}
		if (type === 'mediaclip' && id) {
			this.addFacetFilter('eid', id);
			this.fetchClip(id);
		}
		this.activeType = type || null;
		this.activeId = id || null;
	}

	fetchClip(id: string) {
		this.mediaclipNotFound = false;
		this.sapiService.get('commonclip', id).pipe(
			catchError(err => {
				this.onRequestError(err);
				setTimeout(() => {
					this.mediaclipNotFound = true;
				});
				return of(null);
			})
		).subscribe((clip) => {
			this.mediaclip = clip;
		});
	}

	getRangeFacets(): Subscription {
		const highlights = Object.keys(this.status.highlights);
		const lastHighlightColorRef = { ...this.highlightColorRef };
		this.highlightColorRef = {};
		let colorIndex = 0;
		this.facetHighlightCount = 0;
		this.facetRangeData = {};
		const findColorLaterArr = [];
		const unusedColors = [...this.facetHighlightColors];
		const requests: Observable<any>[] = [];
		highlights.forEach((facetName) => {
			if (this.status.highlights[facetName] && this.status.highlights[facetName].length) {
				this.status.highlights[facetName].forEach((facetValue) => {
					requests.push(this.getFacetRange(facetName, facetValue));
					// set color in ref:
					const key = facetName + ':' + facetValue;
					this.facetHighlightCount++;
					if (lastHighlightColorRef[key]) {
						this.highlightColorRef[key] = lastHighlightColorRef[key];
						const ci = unusedColors.indexOf(lastHighlightColorRef[key]);
						unusedColors.splice(ci, 1);
					} else {
						findColorLaterArr.push(key);
						this.highlightColorRef[key] = this.facetHighlightColors[colorIndex];
					}
					colorIndex++;
				});
			}
		});
		findColorLaterArr.forEach((key, i) => {
			this.highlightColorRef[key] = unusedColors[i];
		});
		return forkJoin(requests).pipe(
			takeUntil(this.httpRequestCanceller)
		).subscribe((result: any[]) => {
			this.facetRangeDataSubject.next(this.facetRangeData);
		});
	}

	parseFacetData(facetData, isMulti = false) {
		for (const facetProperty in facetData) {
			if (facetData[facetProperty]) {
				if (!this.facetValueLabelMap[facetProperty]) {
					this.facetValueLabelMap[facetProperty] = {};
				}
				facetData[facetProperty]?.forEach(facetValueItem => {
					if (facetValueItem?.label) {
						this.facetValueLabelMap[facetProperty][facetValueItem.value] = facetValueItem.label;
					}
				});
				this.facetData[facetProperty] = facetData[facetProperty].length ? facetData[facetProperty] : [];
				if (this.status.highlights[facetProperty]) {
					const highlightExistence = highlightParam => facetData[facetProperty].some(({ value }) => value === highlightParam);
					this.status.highlights[facetProperty].forEach((highlight) => {
						if (!highlightExistence(highlight)) {
							this.toggleFacetValueHighlight(facetProperty, highlight);
							this.loadingFacetRef[facetProperty] = false;
							this.loadingFacetRef[facetProperty + ':' + highlight] = false;
						}
					});
				} else {
					this.facetRangeDataSubject.next(this.facetRangeData);
				}
			}
		}
		// include all filtered in facetvalues if they are 0
		const activeFiltersArr = Object.keys(this.status.filters);
		activeFiltersArr.forEach((facetName) => {
			if (this.status.filters[facetName]) {
				if (!this.facetData[facetName]) {
					this.facetData[facetName] = [];
				}
				this.status.filters[facetName].forEach((facetValue) => {
					if (!this.facetData[facetName].find((facetValueItem) => facetValueItem && facetValueItem.value === facetValue)) {
						this.facetData[facetName].push({
							value: this.facetValueLabelMap[facetName]?.[facetValue] || facetValue,
							count: 0
						});
					}
				});
			}
		});
		if (!facetData || Object.keys(facetData).length === 0) {
			for (const facetProperty in this.facetData) {
				if (this.facetData[facetProperty]) {
					if (this.status.highlights[facetProperty]) {
						this.status.highlights[facetProperty].forEach((value) => {
							this.toggleFacetValueHighlight(facetProperty, value);
							this.loadingFacetRef[facetProperty] = false;
							this.loadingFacetRef[facetProperty + ':' + value] = false;
						});
					}
				}
			}
			this.facetData = {};
		}
		if (isMulti) {
			Object.keys(this.facetData).forEach((facetName) => {
				if (!Object.keys(facetData).includes(facetName)) {
					this.loadingFacetRef[facetName] = false;
					this.facetData[facetName] = [];
				}
			});
		}
		this.parsePercentageViewed();
		this.facetDataSubject.next(this.facetData);
	}

	parsePercentageViewed() {
		if (!this.facetData?.exactPercentageViewed) {
			return;
		}
		this.pctViewedGraphDatasets = [];
		const pvd = this.facetData.exactPercentageViewed.sort((a, b) => a.value > b.value ? 1 : -1);


		// TODO: check if we can get a set of data from the backend that has dropoff numbers instead of how many dropped off in a certain range
		// (Example: 100, 60, 0 instead of 0, 40, 60)
		let rest = this.totalViews;
		if (!rest) {
			return;
		}
		let data = pvd[0] && pvd[0].x === 0 ? [] : [{
			x: 0,
			y: rest
		}];

		// make sure we have a point at 100 pct so we fill all points
		if (!(pvd.find((item) => item.value === 100))) {
			pvd.push({
				value: 100,
				count: 0
			});
		}
		let emptyCheckIndex = 0;
		pvd.forEach((facetData, i) => {
			// fill empty datapoints
			while (emptyCheckIndex && !data[emptyCheckIndex] && emptyCheckIndex < facetData.value) {
				// console.warn('emptyCheckIndex', emptyCheckIndex);
				data[emptyCheckIndex] = {
					x: emptyCheckIndex,
					y: rest
				};
				emptyCheckIndex++;
			}

			if (facetData.value && facetData.value < 101) {
				data.push({ x: facetData.value, y: rest });
			}
			emptyCheckIndex = facetData.value + 1;
			if (facetData.count) {
				rest -= facetData.count;
			}
		});

		data = data.filter((item) => !!item);


		const dataset: ChartDataset = {
			label: this.translate.instant('VIEW_SESSIONS'),
			parsing: false,
			data,
			backgroundColor: ['#3693ef'],
			borderColor: ['#3693ef'],
			borderWidth: 2
		};

		this.pctViewedGraphDatasets.push(dataset);
	}

	toggleFacetValueHighlight(facetName: string, facetValue: string) {
		this.status.highlights[facetName] = this.status.highlights[facetName] || [];
		const valueIndex = this.status.highlights[facetName] && this.status.highlights[facetName].indexOf(facetValue);
		if (!this.status.highlights[facetName].includes(facetValue)) {
			// add
			this.status.highlights[facetName].push(facetValue);
		} else {
			// remove
			if (valueIndex || valueIndex === 0) {
				this.status.highlights[facetName].splice(valueIndex, 1);
				if (this.facetRangeData && this.facetRangeData[facetName] && this.facetRangeData[facetName][facetValue]) {
					delete this.facetRangeData[facetName][facetValue];
					this.facetRangeDataSubject.next(this.facetRangeData);
				}
			}
		}
		this.update();
	}

	addFacetFilter(facetName: string, facetValue: string) {
		this.status.filters[facetName] = this.status.filters[facetName] || [];
		if (!this.status.filters[facetName].includes(facetValue)) {
			this.status.filters[facetName].push(facetValue);
		}
		this.update();
	}

	removeFacetFilter(facetName: string, facetValue?: string) {
		if (facetValue) {
			const valueIndex = this.status.filters[facetName] && this.status.filters[facetName].indexOf(facetValue);
			if (valueIndex || valueIndex === 0) {
				this.status.filters[facetName].splice(valueIndex, 1);
			}
		}
		if (!facetValue || this.status.filters[facetName]?.length === 0) {
			delete this.status.filters[facetName];
		}
		this.update();
	}

	// set granularity based on chosenGranularity and range selected
	// if selected value is not allowed: set to auto
	updateChosenGranularity() {
		if (!this.status.dateRange) {
			if (!this.chosenGranularity) {
				this.chosenGranularity = 'day';
			}
			return;
		}
		const rangeSeconds = (new Date(this.status.dateRange.toDate).getTime() - new Date(this.status.dateRange.fromDate).getTime()) / 1000;

		if (this.status.granularity === 'auto') {
			this.chosenGranularity = this.getAutoGranularityValue(rangeSeconds);
			return;
		}

		const allowed = this.getGranularityAllowed(this.status.granularity, rangeSeconds);

		if (allowed) {
			this.chosenGranularity = this.status.granularity;
		} else {
			this.status.granularity = 'auto';
			this.chosenGranularity = this.getAutoGranularityValue(rangeSeconds);
		}
	}

	// minutes/hours only if range small enough
	// day/week/month/year only if range large enough
	public getGranularityAllowed(granularity: AnalyticsGranularity, rangeSeconds: number): boolean {
		let allowed = false;
		switch (granularity) {
			case 'auto':
				allowed = true;
				break;
			case 'minute':
				allowed = rangeSeconds < 7 * 24 * 3600;
				break;
			case 'hour':
				allowed = rangeSeconds < 30 * 24 * 3600;
				break;
			case 'day':
				allowed = rangeSeconds > 1 * 24 * 3600;
				break;
			case 'week':
				allowed = rangeSeconds > 7 * 24 * 3600;
				break;
			case 'month':
				allowed = rangeSeconds > 30 * 24 * 3600;
				break;
			case 'year':
				allowed = rangeSeconds > 365 * 24 * 3600;
				break;
		}
		return allowed;
	}

	getAutoGranularityValue(rangeSeconds: number): AnalyticsGranularity {
		if (rangeSeconds <= 3 * 3600) { // 3 hours
			return 'minute';
		}
		if (rangeSeconds < 8 * 24 * 3600) { // 8 days
			return 'hour';
		}
		if (rangeSeconds < 100 * 24 * 3600) { // 100 days
			return 'day';
		}
		return 'week';
	}

	setGranularity(granularity: AnalyticsGranularity = 'auto', updateData = true) {
		if (this.status.granularity !== granularity || (granularity === 'auto' && this.chosenGranularity !== this.status.granularity)) {
			this.status.granularity = granularity;
			this.updateChosenGranularity();
			if (updateData) {
				this.update();
			}
			return;
		}

		this.status.granularity = granularity;
		this.updateChosenGranularity();
	}

	getFormattedDate(granularity: AnalyticsGranularity, dateString: string, period: any, items: AnalyticsAPIResponse['body']['items']) {
		let hideDay = false;
		let hideMonth = false;
		const y = (new Date()).getFullYear();
		let hideYear = (new Date(period.fromDate)).getFullYear() === y;
		if (granularity === 'month' || granularity === 'year') {
			if (granularity === 'year') {
				hideMonth = true;
				hideYear = false;
			}
			hideDay = true;
		}
		const format = (granularity === 'hour' || granularity === 'minute') ? 'date-time' : 'date';
		// When the granularity is set to week or hour we need to check the upcoming dateString as well, to have a from and a to date
		// for each specific week/hour
		if (granularity === 'week' || granularity === 'hour') {
			let toDate = dateString;
			for (let i = 0; i < items.length; i++) {
				if (dateString === items[i].datetime) {
					if (items[i + 1]) {
						toDate = items[i + 1].datetime;
					}
				}
			}
			if (granularity === 'hour') {
				if (toDate === dateString) {
					toDate = dayjs(toDate).add(1, 'hour').toISOString();
				}
				hideDay = true;
				hideMonth = true;
				return (this.datePipe.transform(
					dateString, format, 'short', { hideYear }) + ' - ' + this.datePipe.transform(toDate, format, 'short', { hideYear, hideMonth, hideDay }
				));
			} else {
				if (toDate === dateString) {
					toDate = period.toDate;
				} else {
					toDate = dayjs(toDate).subtract(1, 'days').toISOString();
				}
				return (this.datePipe.transform(
					dateString, format, 'short', { hideYear }) + ' - ' + this.datePipe.transform(toDate, format, 'short', { hideYear })
				);
			}
		}
		return this.datePipe.transform(dateString, format, 'short', { hideYear, hideMonth, hideDay });
	}

	getClipRangeStats(id: string, noFacets = false): Observable<AnalyticsRangeDataset> {
		this.loadingData = true;
		this.metrics.views.count = this.totalViews = null;
		this.metrics.views.loading = true;

		this.setParams();

		if (noFacets) {
			this.params = this.params
				.delete('facet')
				.delete('facetdepth')
				.delete('facetconfig');
		}

		return this.sapiService.getUrl(`/analytics/range/mediaclip/${id || ''}`, this.params).pipe(
			catchError(err => {
				console.error('Error in getClipRangeStats', err);
				this.loadingData = false;
				this.metrics.views.loading = false;
				this.facetData = {};
				this.onRequestError(err);
				return of(null);
			}),
			map((data: AnalyticsAPIResponse) => {
				this.loadingData = false;
				if (!data) {
					this.facetDataSubject.next(null);
					this.rangeDataSubject.next(null);
					return null;
				}
				const analyticsRangeDataset: AnalyticsRangeDataset = {
					total: 0,
					type: 'MediaClip',
					chartDataset: {
						label: this.translate.instant('VIEWS'),
						parsing: false,
						data: [],
						backgroundColor: [id ? '#7ab1df' : '#3693ef'],
						borderColor: [id ? '#7ab1df' : '#3693ef'],
						borderWidth: 2
					}
				};
				this.metrics.views.count = this.totalViews = data?.body?.total || 0;
				this.metrics.views.loading = false;
				this.parseFacetData(data?.body?.facets || {}, true);
				if (!data.body || data.body.items === null) {
					this.rangeDataSubject.next(null);
					return analyticsRangeDataset;
				}

				const dataSet = data.body.items.map(item => ({
					x: this.getFormattedDate(this.chosenGranularity, item.datetime, data.period, data.body.items),
					y: item.total
				}));
				this.metrics.views.count = this.totalViews = analyticsRangeDataset.total = data.body.total || 0;
				analyticsRangeDataset.chartDataset.data = dataSet as any;
				this.rangeDataSubject.next(analyticsRangeDataset);

				return analyticsRangeDataset;
			})
		);
	}

	setFilterConfigs() {
		// note: solrType is currenlty obsolete because handled in backend
		this.filterConfigs = [
			// Content
			{ label: 'Title', name: 'title_ci', category: 'Content', type: 'solr', aliases: ['Name'] },
			{ label: 'Clip ID', name: 'eid', category: 'Content', type: 'solr', aliases: ['Content ID'] },
			{ label: 'Duration', name: 'clipLength', category: 'Content', type: 'solr', aliases: ['Length'] },
			{ label: 'Media type', name: 'mediatype', category: 'Content', type: 'solr'},
			{ label: 'Source type', name: 'sourcetype', category: 'Content', type: 'solr', aliases: ['Live / On demand'] },
			// Audience
			{ label: 'Publication', name: 'publication', category: 'Audience', type: 'solr', allstatsOnly: true, aliases: ['Client handle'] },
			{ label: 'Country', name: 'country', category: 'Audience', type: 'solr', aliases: ['Location'] },
			{ label: 'Region', name: 'region', category: 'Audience', type: 'solr', aliases: ['Location'] },
			{ label: 'City', name: 'city', category: 'Audience', type: 'solr', aliases: ['Location'] },
			{ label: 'Web domain', name: 'domain', category: 'Audience', type: 'solr', aliases: ['Website'] },
			{ label: 'Referring Web domain', name: 'rdomain', category: 'Audience', type: 'solr', aliases: ['Referring website'] },
			{ label: 'Web address', name: 'referrer', category: 'Audience', type: 'solr', aliases: ['URL'] },
			{ label: 'Referring Web address', name: 'rreferrer', category: 'Audience', type: 'solr', aliases: ['URL'] },
			// Context
			{ label: 'Playout', name: 'playout', category: 'Context', type: 'solr', aliases: ['Player'] },
			{ label: 'Project', name: 'projectName', category: 'Context', type: 'solr', idMapped: true },
			{ label: 'Playlist', name: 'playlistName', category: 'Context', type: 'solr', idMapped: true },
			{ label: 'Channel', name: 'channelName', category: 'Context', type: 'solr', idMapped: true},
			{ label: 'Project Context', name: 'projectContext', category: 'Context', type: 'solr' },
			{ label: 'Initiator', name: 'initiator', category: 'Context', type: 'solr' },
			// Technology
			{ label: 'Device type', name: 'deviceType', category: 'Technology', type: 'solr', aliases: ['Mobile / Desktop'] },
			{ label: 'Operating system', name: 'osName', category: 'Technology', type: 'solr', aliases: ['OS'] },
			{ label: 'Operating system version', name: 'osVersion', category: 'Technology', type: 'solr', aliases: ['OS version'] },
			{ label: 'Browser', name: 'browserName', category: 'Technology', type: 'solr' },
			{ label: 'Browser version', name: 'browserVersion', category: 'Technology', type: 'solr' },
			{ label: 'Video quality', name: 'bitrate', solrType: 'int', category: 'Technology', type: 'solr', aliases: ['Playback qualite'] },
			{ label: 'Screen resolution', name: 'resolution', category: 'Technology', type: 'solr', aliases: ['Screen size'] },
			{ label: 'Playmode', name: 'playmode', category: 'Technology', type: 'solr' },
			{ label: 'Autoplay', name: 'autoPlay', solrType: 'bool', category: 'Technology', type: 'solr', aliases: ['Autostart'] },
			{ label: 'Automute', name: 'autoMute', solrType: 'bool', category: 'Technology', type: 'solr', aliases: ['Automute', 'No Volume'] },
			{ label: 'Fullscreen', name: 'fullscreen', solrType: 'bool', category: 'Technology', type: 'solr' },
			{ label: 'Autoloop', name: 'autoLoop', solrType: 'bool', category: 'Technology', type: 'solr', aliases: ['Replay'] },
			{ label: 'Mobile Brand', name: 'mobileBrand', category: 'Technology', type: 'solr' },
			{ label: 'Mobile Model', name: 'mobileMarketingName', category: 'Technology', type: 'solr' },
			// Engagement
			{ label: 'Moving thumbnail played', name: 'movingThumbnailPlayed', category: 'Engagement', type: 'solr' },
			{ label: 'Main content started', name: 'mainContentStarted', category: 'Engagement', type: 'solr', aliases: ['Content played'] },
			{ label: 'Percentage reached', name: 'percentageViewed', solrType: 'int', category: 'Engagement', type: 'solr' },
			{ label: 'Time reached', name: 'timeReached', solrType: 'long', category: 'Engagement', type: 'solr' },
			{ label: 'Time viewed', name: 'preciseViewTimeRange', solrType: 'long', category: 'Engagement', type: 'solr' },
			{ label: 'Completed', name: 'finished', category: 'Engagement', type: 'solr', aliases: ['Finished'] },
			{ label: 'Has interactivity', name: 'hasInteractivity', category: 'Engagement', type: 'solr', aliases: ['Is interactive'] },
			{ label: 'Custom event', name: 'customStats', solrType: 'txtmulti', category: 'Engagement', type: 'solr' },
			{ label: 'User ID', name: 'uid', category: 'Engagement', type: 'solr' },
			{ label: 'Player session ID', name: 'playerSession', category: 'Engagement', type: 'solr' },
			{ label: 'View session ID', name: 'sid', category: 'Engagement', type: 'solr' },
			{ label: 'Unmuted', name: 'unmuted', category: 'Engagement', type: 'solr', aliases: ['Sound'] },
			{ label: 'Player functions', name: 'playerActions', category: 'Engagement', type: 'solr' },
			// Ad units
			{ label: 'VAST Quartiles', name: 'vastQuartiles', solrType: 'txtmulti', category: 'Ad units', type: 'solr' },
			{ label: 'VAST Service click', name: 'vastClicked', category: 'Ad units', type: 'solr' },
			{ label: 'Views with Ad unit inits', name: 'adunitInits', category: 'Ad units', type: 'solr' },
			{ label: 'Views with Line item inits', name: 'lineitemInits', solrType: 'txtmulti', category: 'Ad units', type: 'solr' },
			{ label: 'Views with Creative impressions', name: 'creativeStarts', solrType: 'txtmulti', category: 'Ad units', type: 'solr' },
			{ label: 'Views with Creative clicks', name: 'adunitCreativeClicks', solrType: 'txtmulti', category: 'Ad units', type: 'solr' },
			{ label: 'Views with Creative skips', name: 'adunitCreativeSkips', solrType: 'txtmulti', category: 'Ad units', type: 'solr' },
			{ label: 'Views with Creative errors', name: 'adunitCreativeErrors', solrType: 'txtmulti', category: 'Ad units', type: 'solr' },
			{ label: 'Views with Ad System', name: 'adunitCreativeSystemStarts', solrType: 'txtmulti', category: 'Ad units', type: 'solr' },
			{ label: 'Views with Ad Blocker detected', name: 'adunitAdBlockerDetected', category: 'Ad units', type: 'solr' },
			// Ad schedules
			{ label: 'Views with Ad schedule inits', name: 'adscheduleInits', solrType: 'txtmulti', category: 'Ad schedules', type: 'solr', hidden: true },
			{ label: 'Views with Creative impressions', name: 'adscheduleCreativeStarts', solrType: 'txtmulti', category: 'Ad schedules', type: 'solr', hidden: true },
			{ label: 'Views with Creative clicks', name: 'adscheduleCreativeClicks', solrType: 'txtmulti', category: 'Ad schedules', type: 'solr', hidden: true },
			{ label: 'Views with Creative skips', name: 'adscheduleCreativeSkips', solrType: 'txtmulti', category: 'Ad schedules', type: 'solr', hidden: true },
			{ label: 'Views with Creative errors', name: 'adscheduleCreativeErrors', solrType: 'txtmulti', category: 'Ad schedules', type: 'solr', hidden: true },
			{ label: 'Views with Ad Blocker detected', name: 'adscheduleAdBlockerDetected', category: 'Ad schedules', type: 'solr', hidden: true },

			// // Commercial
			// { label: 'VAST Quartiles', name: 'vastQuartiles', solrType: 'txtmulti', category: 'Commercial', type: 'solr' },
			// { label: 'VAST Service click', name: 'vastClicked', category: 'Commercial', type: 'solr' },
			// { label: 'Views with Ad unit inits', name: 'adunitInits', category: 'Commercial', type: 'solr' },
			// { label: 'Views with Line item inits', name: 'lineitemInits', solrType: 'txtmulti', category: 'Commercial', type: 'solr' },
			// { label: 'Views with Creative impressions', name: 'creativeStarts', solrType: 'txtmulti', category: 'Commercial', type: 'solr' },
			// { label: 'Views with Creative clicks', name: 'creativeClicks', solrType: 'txtmulti', category: 'Commercial', type: 'solr' },
			// { label: 'Views with Creative skips', name: 'creativeSkips', solrType: 'txtmulti', category: 'Commercial', type: 'solr' },
			// { label: 'Views with Creative errors', name: 'creativeErrors', solrType: 'txtmulti', category: 'Commercial', type: 'solr' },
			// { label: 'Views with Ad System', name: 'creativeSystemStarts', solrType: 'txtmulti', category: 'Commercial', type: 'solr' },
			// { label: 'Views with Ad Blocker detected', name: 'adBlockerDetected', category: 'Commercial', type: 'solr' }

			// { label: 'Use type', name: 'useType', category: 'Content', type: 'solr' },
			// { label: 'Missing stats', name: 'missingSerials', solrType: 'long', category: 'Engagement', type: 'solr', allstatsOnly: true },
			// { label: 'Nr of missing sn\'s', name: 'missingSerials', solrType: 'long', category: 'Engagement', type: 'solr', allstatsOnly: true },
			// { label: 'Highest sn', name: 'serialMax', solrType: 'long', category: 'Engagement', type: 'solr' },
			// { label: 'Play failed', name: 'playFailed', category: 'Engagement', type: 'solr', allstatsOnly: true },
			// { label: 'Play failed message', name: 'playFailedMsg', category: 'Engagement', type: 'solr', allstatsOnly: true },
			// VMS FACETS
			// { label: 'Author', name: 'authorSort', category: 'OVP Content', type: 'vms' },
			// { label: 'Copyright', name: 'copyrightSort', category: 'OVP Content', type: 'vms' },
			// { label: 'Status', name: 'statusSort', category: 'MediaClip', type: 'vms' },
			// { label: 'Tag', name: 'catSort', category: 'OVP Content', type: 'vms' },
			// { label: 'Rating', name: 'rating', category: 'MediaClip', type: 'vms' },
			// { label: 'Export channel', name: 'exportchannelid_multistring', category: 'OVP Content', type: 'vms', vmsResolveName: 'ExportChannels' },
			// { label: 'Export system', name: 'exportsystemid_multistring', category: 'OVP Content', type: 'vms', vmsResolveName: 'ExportSystems' }
		];

		const fieldTypesSolrSuffix = {
			// only supported custom fields that can be logged to stats for now:
			// eslint-disable-next-line id-blacklist
			string: '_str',
			// eslint-disable-next-line id-blacklist
			boolean: '_bool'
		};
		this.publicationService.getData().subscribe((publication: Publication) => {
			this.isAllstats = publication?.name === 'allstats';
			if (publication && publication.customfields && publication.customfields.mediaclip && publication.customfields.mediaclip.length) {
				const lengthPre = this.filterConfigs.length;
				publication.customfields.mediaclip.filter((f) => f.includeInStats).forEach((field) => {
					const cfg = { label: field.label, name: field.name + fieldTypesSolrSuffix[field.type], category: 'Custom fields', type: 'solr' };
					this.filterConfigs.push(cfg);
					this.facetConfigs[cfg.name] = cfg;
				});
				if (lengthPre !== this.filterConfigs.length) {
					this.filterConfigChange.next(this.filterConfigs);
				}
			}
		});
		this.sapiService.query('adschedule', [], 0, 1).subscribe((res) => {
			if (res?.items?.length) {
				this.filterConfigs.forEach(config => {
					if (config.category === 'Ad schedules') {
						config.hidden = false;
					}
				});
				this.filterConfigChange.next(this.filterConfigs);
			}
		});
		this.filterConfigChange.next(this.filterConfigs);
	}

	getFacetGroups() {
		this.facetGroups = [];
		const uniqueCats = [...new Set(this.filterConfigs.filter(item => !item.hidden).map(item => item.category))];
		uniqueCats.forEach(cat => {
			const group = {
				title: cat,
				showLimit: 6,
				filters: this.filterConfigs.filter(filterConfig => filterConfig.category === cat)
			};
			this.facetGroups.push(group);
		});
		return this.facetGroups;
	}
}
